// Sync status indicator component
// Shows offline status and pending upload/survey count

import { useState, useEffect } from 'react'
import useSurveyStore from '../stores/surveyStore'

export default function SyncStatus({ isOnline, pendingUploads, pendingSurveys = 0, syncing, syncingSurveys = false }) {
  const syncPendingPhotos = useSurveyStore(state => state.syncPendingPhotos)
  const syncPendingSurveys = useSurveyStore(state => state.syncPendingSurveys)
  const refreshPendingCount = useSurveyStore(state => state.refreshPendingCount)
  const resetSyncStatus = useSurveyStore(state => state.resetSyncStatus)
  const [syncDuration, setSyncDuration] = useState(0)
  const [lastError, setLastError] = useState(null)

  // Track how long sync has been running
  const isSyncingAny = syncing || syncingSurveys
  useEffect(() => {
    let interval
    if (isSyncingAny) {
      setSyncDuration(0)
      interval = setInterval(() => {
        setSyncDuration(d => d + 1)
      }, 1000)
    } else {
      setSyncDuration(0)
    }
    return () => clearInterval(interval)
  }, [isSyncingAny])

  const handleSync = async () => {
    setLastError(null)
    // Sync both surveys and photos
    if (pendingSurveys > 0) {
      const surveyResult = await syncPendingSurveys()
      if (surveyResult && !surveyResult.success && surveyResult.error) {
        setLastError(surveyResult.error)
        return
      }
    }
    if (pendingUploads > 0) {
      const photoResult = await syncPendingPhotos()
      if (photoResult && !photoResult.success && photoResult.error) {
        setLastError(photoResult.error)
      }
    }
  }

  const handleForceReset = async () => {
    console.log('Force reset clicked')
    setLastError(null)
    setSyncDuration(0)

    try {
      // Force reset sync status
      resetSyncStatus()

      // Wait a moment for state to update
      await new Promise(resolve => setTimeout(resolve, 100))

      // Refresh counts
      await refreshPendingCount()

      console.log('Force reset complete')
    } catch (error) {
      console.error('Force reset failed:', error)
      setLastError('Reset failed: ' + error.message)
    }
  }

  // Refresh count on mount and periodically
  useEffect(() => {
    refreshPendingCount()
    const interval = setInterval(refreshPendingCount, 30000) // Check every 30s
    return () => clearInterval(interval)
  }, [refreshPendingCount])

  // Don't show anything if online and nothing pending
  const totalPending = pendingUploads + pendingSurveys
  const isSyncing = syncing || syncingSurveys

  if (isOnline && totalPending === 0 && !isSyncing && !lastError) {
    return null
  }

  const syncStuck = isSyncing && syncDuration > 15 // Show force option after 15s

  return (
    <div className="sync-status-bar">
      {!isOnline && (
        <div className="sync-status offline">
          <span className="status-icon">&#x26A0;</span>
          <span>Offline - data saved locally</span>
        </div>
      )}

      {isOnline && isSyncing && (
        <div className="sync-status syncing">
          <span className="status-icon spinning">&#x21BB;</span>
          <span>
            {syncingSurveys ? 'Syncing surveys...' : 'Syncing photos...'}
            {syncDuration > 5 ? ` (${syncDuration}s)` : ''}
          </span>
          {syncStuck && (
            <button className="sync-button force" onClick={handleForceReset}>
              Force Reset
            </button>
          )}
        </div>
      )}

      {isOnline && !isSyncing && totalPending > 0 && (
        <div className="sync-status pending">
          <span className="status-icon">&#x2601;</span>
          <span>
            {pendingSurveys > 0 && `${pendingSurveys} survey${pendingSurveys !== 1 ? 's' : ''}`}
            {pendingSurveys > 0 && pendingUploads > 0 && ', '}
            {pendingUploads > 0 && `${pendingUploads} photo${pendingUploads !== 1 ? 's' : ''}`}
            {' '}pending
          </span>
          <button className="sync-button" onClick={handleSync}>
            Sync Now
          </button>
        </div>
      )}

      {lastError && (
        <div className="sync-status error">
          <span className="status-icon">&#x26A0;</span>
          <span>Sync error: {lastError}</span>
          <button className="sync-button" onClick={() => setLastError(null)}>
            Dismiss
          </button>
        </div>
      )}

      <style>{`
        .sync-status-bar {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 1000;
        }

        .sync-status {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 16px;
          font-size: 14px;
          font-weight: 500;
        }

        .sync-status.offline {
          background: #FEF3C7;
          color: #92400E;
          border-top: 1px solid #F59E0B;
        }

        .sync-status.syncing {
          background: #DBEAFE;
          color: #1E40AF;
          border-top: 1px solid #3B82F6;
        }

        .sync-status.pending {
          background: #FEE2E2;
          color: #991B1B;
          border-top: 1px solid #EF4444;
        }

        .status-icon {
          font-size: 18px;
        }

        .status-icon.spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .sync-button {
          margin-left: 12px;
          padding: 8px 16px;
          background: #991B1B;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
          min-width: 100px;
          min-height: 44px;
        }

        .sync-button:hover {
          background: #7F1D1D;
        }

        .sync-button:active {
          transform: scale(0.95);
        }

        .sync-button.force {
          background: #DC2626;
        }

        .sync-button.force:hover {
          background: #B91C1C;
        }

        .sync-button.force:active {
          background: #991B1B;
        }

        .sync-status.error {
          background: #FEE2E2;
          color: #991B1B;
          border-top: 1px solid #EF4444;
        }
      `}</style>
    </div>
  )
}
